import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles } from "lucide-react";

interface RegenerateFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegenerate: (feedback: string) => Promise<void>;
  type: "audience" | "messages";
  isLoading?: boolean;
}

export function RegenerateFeedbackDialog({
  open,
  onOpenChange,
  onRegenerate,
  type,
  isLoading = false,
}: RegenerateFeedbackDialogProps) {
  const [feedback, setFeedback] = useState("");

  const handleRegenerate = async () => {
    await onRegenerate(feedback);
    setFeedback("");
    onOpenChange(false);
  };

  const title = type === "audience" 
    ? "Improve Target Audience" 
    : "Improve Message Sequence";

  const placeholder = type === "audience"
    ? "E.g., Focus more on enterprise companies, add more senior titles, include fintech industry..."
    : "E.g., Make messages more casual, focus on ROI benefits, shorter follow-ups...";

  const description = type === "audience"
    ? "Tell us what you'd like to improve about the target audience. Be specific about industries, job titles, company sizes, or any other criteria."
    : "Tell us what you'd like to improve about the messages. Be specific about tone, length, call-to-action, or key points to emphasize.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={placeholder}
            rows={4}
            className="resize-none"
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            The more specific your feedback, the better the results.
          </p>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerate}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Regenerate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
